import { AppsFlyer } from 'appsflyer-capacitor-plugin';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

type WindowWithFbq = Window & { fbq?: (...args: unknown[]) => void };

/** 将 AppsFlyer 风格参数转为 Meta Pixel trackCustom 可用的扁平值 */
function toMetaPixelCustomParams(values: Record<string, any>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(values)) {
    if (val === undefined || val === null) continue;
    const t = typeof val;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      out[key] = val;
    } else {
      try {
        out[key] = t === 'object' ? JSON.stringify(val) : String(val);
      } catch {
        out[key] = String(val);
      }
    }
  }
  return out;
}

/** Meta Pixel 自定义事件（仅 Web；fbq 由 index.html 加载） */
export function metaPixelTrackCustom(eventName: string, params: Record<string, unknown> = {}) {
  if (isNative || typeof window === 'undefined') return;
  const fbq = (window as WindowWithFbq).fbq;
  if (typeof fbq !== 'function') {
    console.warn('[Analytics] Meta Pixel fbq unavailable, skipped:', eventName);
    return;
  }
  fbq('trackCustom', eventName, params);
}

/**
 * 在用户登录等「已有 stable user id / 邮箱」之后再调用。
 *
 * index.html 里的首次 `fbq('init', pixelId)` 无法带 external_id/em（此时还没有用户），这是预期行为。
 * Meta 允许对同一 pixelId **再次** `fbq('init', pixelId, userData)`，把 Advanced Matching 补上；
 * 随后发出的 track / trackCustom 会带上更新后的匹配信息。
 */
export function metaPixelSyncAdvancedMatching(external_id: string, email?: string | null) {
  if (isNative || typeof window === 'undefined') return;
  const fbq = (window as WindowWithFbq).fbq;
  if (typeof fbq !== 'function') {
    console.warn('[Analytics] Meta Pixel fbq unavailable, skipped: advanced matching');
    return;
  }
  const pixelId = ((import.meta as any).env?.VITE_META_PIXEL_ID as string | undefined)?.trim();
  if (!pixelId) return;

  const userData: Record<string, string> = { external_id };
  if (email?.trim()) userData.em = email.trim().toLowerCase();
  fbq('init', pixelId, userData);
}

/** Firebase Google Web 登录成功回调路径：先同步 Advanced Matching，再发无 PII 的自定义事件 */
export function logMetaPixelGoogleWebLoginOutcome(
  isNewFirebaseUser: boolean,
  firebaseUserId: string,
  email?: string | null
) {
  if (isNative || typeof window === 'undefined') return;
  const fbq = (window as WindowWithFbq).fbq;
  if (typeof fbq !== 'function') {
    console.warn('[Analytics] Meta Pixel fbq unavailable, skipped: google web login');
    return;
  }

  metaPixelSyncAdvancedMatching(firebaseUserId, email);

  const eventName = isNewFirebaseUser
    ? 'NewUserLogInWithGoogleWeb'
    : 'ExistingUserLogInWithGoogleWeb';
  fbq('trackCustom', eventName, { login_provider: 'google' });
}

/**
 * Initialize AppsFlyer Analytics
 * Note: Native initialization happens in AppDelegate.swift / MainActivity.java
 * This JS initialization acts as a bridge and for additional configuration if needed.
 * But according to the plugin docs, `initSdk` should be called to start the JS bridge listener.
 */
export async function initAnalytics() {
  if (!isNative) {
    console.log('[Analytics] Web platform detected, skipping AppsFlyer init');
    return;
  }

  try {
    // Info.plist / AndroidManifest.xml should handle the keys usually,
    // but the plugin often requires initSdk to be called to fully attach.
    // However, looking at the provided AppDelegate.swift, AppsFlyerLib.shared().start() is called natively.
    // The Capacitor plugin documentation usually suggests calling initSdk with devKey/appId primarily for the plugin to sync.
    // Since we saw native code doing the start, we might just need to ensure the JS side knows it's ready.
    // But the standard practice for this plugin is to call initSdk from JS as well.
    // We will pull the keys from environment or hardcode if they aren't exposed to JS,
    // but better yet, let's rely on native unless we finding issues.
    // Actually, waiting, standard `appsflyer-capacitor-plugin` usage involves calling `initSdk`.

    // Let's implement a safe init that logs success/failure.
    console.log('[Analytics] Initializing AppsFlyer JS SDK...');

    const IOS_APP_ID = (import.meta as any).env?.VITE_APPSFLYER_IOS_APP_ID;
    const DEV_KEY = (import.meta as any).env?.VITE_APPSFLYER_DEV_KEY;

    if (!DEV_KEY || (Capacitor.getPlatform() === 'ios' && !IOS_APP_ID)) {
        console.warn('[Analytics] Missing AppsFlyer config (VITE_APPSFLYER_DEV_KEY / VITE_APPSFLYER_IOS_APP_ID)');
        // We might choose to return here, or assume native handles it.
        // But for `logEvent` to work reliably via the plugin, initSdk is usually expected.
        // Let's rely on native bridge if JS config is missing, but log a warning.
    }

    // Note: If you call initSdk here, it might re-initialize or conflict if not handled carefully with native start.
    // Since AppDelegate.swift handles initialization and ATT user authorization, we should SKIP JS initialization for iOS
    // to avoid double-start or configuration overwrites.

    if (Capacitor.getPlatform() === 'ios') {
        console.log('[Analytics] iOS detected: Relying on native AppDelegate initialization. Skipping JS initSDK.');
        return;
    }

    // Using `initSdk` is the standard way for other platforms (Android/Web).
    const options = {
      devKey: DEV_KEY || 'REPLACE_ME_IF_NOT_IN_ENV',
      appID: IOS_APP_ID || 'REPLACE_ME_IF_NOT_IN_ENV',
      isDebug: true,
      onInstallConversionDataListener: true
    };

    if (DEV_KEY) {
        // Only init if we have keys (mostly for Android here)
        await AppsFlyer.initSDK(options);
        console.log('[Analytics] AppsFlyer JS SDK initialized');
    }

  } catch (error) {
    console.error('[Analytics] Failed to initialize AppsFlyer:', error);
  }
}

/**
 * Log a custom event
 * @param eventName Name of the event (e.g. 'af_purchase', 'voice_session_start')
 * @param eventValues Object containing event parameters
 */
export async function logEvent(eventName: string, eventValues: Record<string, any> = {}) {
  // Always log to console for debugging
  console.log(`[Analytics] Logging Event: ${eventName}`, eventValues);

  if (!isNative) {
    metaPixelTrackCustom(eventName, toMetaPixelCustomParams(eventValues));
    return;
  }

  try {
    await AppsFlyer.logEvent({ eventName, eventValue: eventValues });
  } catch (error) {
    console.error(`[Analytics] Failed to log event ${eventName}:`, error);
  }
}

/**
 * Set Customer User ID matches the one used in Auth/Active RevenueCat
 * @param userId Unique User ID
 */
export async function setCustomerUserId(userId: string) {
    console.log(`[Analytics] Setting CUID: ${userId}`);
    if (!isNative) return;

    try {
        await AppsFlyer.setCustomerUserId({ cuid: userId });
    } catch (error) {
        console.error('[Analytics] Failed to set CUID:', error);
    }
}
